import {
  Color3,
  CreateBox,
  ImportMeshAsync,
  Mesh,
  Quaternion,
  Scene,
  StandardMaterial,
  TransformNode,
  Vector3
} from "../../app/babylon";
import "@babylonjs/loaders/glTF";
import { assets } from "../assets";
import type { Slope, SlopeSample } from "../world/Slope";

const GRAVITY = 22;
const MAX_SPEED = 16;
const LEAN_MAX = 0.72;
const LEAN_RATE = 3.0;
const TURN_GAIN = 0.06;
const LATERAL_GRIP = 1.4;
const SCRUB_GAIN = 0.12;
const POWDER_FWD_DRAG = 0.9;
const POWDER_LAT_DRAG = 4.5;
const POWDER_PUSH = 22;
const BOARD_OFFSET = 0.07;
const RIDER_HEIGHT_PIVOT = 0.0;
const CRASH_SPIN = 7;

export interface RiderInputs {
  left: boolean;
  right: boolean;
  leanTarget?: number;
}

export class Rider {
  readonly root: TransformNode;
  private modelRoot!: TransformNode;
  private shadowable: Mesh[] = [];

  position = new Vector3(0, 0, 4);
  velocity = new Vector3(0, 0, 6);
  heading = 0;
  lean = 0;
  onGround = true;
  crashed = false;
  crashTimer = 0;

  constructor(private readonly scene: Scene) {
    this.root = new TransformNode("rider-root", scene);
    this.root.position = this.position.clone();
    this.modelRoot = new TransformNode("rider-model", scene);
    this.modelRoot.parent = this.root;
  }

  async load(): Promise<void> {
    const result = await ImportMeshAsync(assets.riderGlb, this.scene);

    const rootMesh = result.meshes.find((m) => m.parent == null);
    if (!rootMesh) {
      throw new Error("rider GLB has no root mesh");
    }

    const zSign = Math.sign(rootMesh.scaling.z) || 1;
    rootMesh.scaling.set(1, 1, zSign);
    rootMesh.position.set(0, 0, 0);
    for (const m of result.meshes) {
      if (m === rootMesh) continue;
      m.position.set(0, 0, 0);
      m.scaling.set(1, 1, 1);
      m.rotationQuaternion = null;
      m.rotation.set(0, 0, 0);
      m.isPickable = false;
      if (m instanceof Mesh && m.getTotalVertices() > 0) {
        this.shadowable.push(m);
      }
    }
    rootMesh.isPickable = false;
    rootMesh.parent = this.modelRoot;

    this.modelRoot.computeWorldMatrix(true);
    let min = new Vector3(Infinity, Infinity, Infinity);
    let max = new Vector3(-Infinity, -Infinity, -Infinity);
    let havePoints = false;
    for (const m of result.meshes) {
      if (!(m instanceof Mesh)) continue;
      if (m.getTotalVertices() === 0) continue;
      m.computeWorldMatrix(true);
      const bb = m.getBoundingInfo().boundingBox;
      min = Vector3.Minimize(min, bb.minimumWorld);
      max = Vector3.Maximize(max, bb.maximumWorld);
      havePoints = true;
    }
    if (!havePoints) {
      min = new Vector3(-0.3, 0, -0.3);
      max = new Vector3(0.3, 1, 0.3);
    }

    const sizeY = max.y - min.y;
    const targetHeight = 2.2;
    const scale = targetHeight / Math.max(sizeY, 0.001);

    rootMesh.scaling.set(scale, scale, scale * zSign);
    rootMesh.position.set(0, -min.y * scale + BOARD_OFFSET, 0);

    const yawFlip = Quaternion.RotationYawPitchRoll(Math.PI, 0, 0);
    rootMesh.rotationQuaternion = rootMesh.rotationQuaternion
      ? yawFlip.multiply(rootMesh.rotationQuaternion)
      : yawFlip;

    if (this.shadowable.length === 0) {
      const placeholder = CreateBox("rider-placeholder", { size: 0.6 }, this.scene);
      const placeholderMat = new StandardMaterial("rider-placeholder-mat", this.scene);
      placeholderMat.diffuseColor = new Color3(0.9, 0.2, 0.2);
      placeholder.material = placeholderMat;
      placeholder.parent = this.modelRoot;
      this.shadowable.push(placeholder);
    }
  }

  shadowMeshes(): Mesh[] {
    return this.shadowable;
  }

  resetTo(z: number, slope: Slope): void {
    const sample = slope.sampleAtZ(z);
    this.position.set(sample.centerX, sample.surfaceY + BOARD_OFFSET, z);
    this.heading = Math.atan2(sample.forwardX, sample.forwardZ);
    this.lean = 0;
    this.velocity.set(sample.forwardX * 6, 0, sample.forwardZ * 6);
    this.onGround = true;
    this.crashed = false;
    this.crashTimer = 0;
  }

  triggerCrash(): void {
    if (this.crashed) return;
    this.crashed = true;
    this.crashTimer = 0;
  }

  step(dt: number, slope: Slope, inputs: RiderInputs): void {
    if (this.crashed) {
      this.crashTimer += dt;
      this.velocity.x *= Math.max(0, 1 - 3 * dt);
      this.velocity.z *= Math.max(0, 1 - 3 * dt);
      this.velocity.y -= GRAVITY * dt * 0.5;
      this.position.x += this.velocity.x * dt;
      this.position.z += this.velocity.z * dt;
      this.position.y += this.velocity.y * dt;

      const sampleC = slope.sampleAtZ(this.position.z);
      const minY = sampleC.surfaceY + BOARD_OFFSET;
      if (this.position.y < minY) {
        this.position.y = minY;
        if (this.velocity.y < 0) this.velocity.y = 0;
      }

      this.root.position.copyFrom(this.position);
      this.modelRoot.rotationQuaternion = Quaternion.RotationYawPitchRoll(
        this.heading,
        this.crashTimer * 1.6,
        this.crashTimer * CRASH_SPIN
      );
      return;
    }

    const binaryTarget = (inputs.left ? -LEAN_MAX : 0) + (inputs.right ? LEAN_MAX : 0);
    const targetLean =
      inputs.leanTarget !== undefined
        ? Math.max(-LEAN_MAX, Math.min(LEAN_MAX, inputs.leanTarget * LEAN_MAX))
        : binaryTarget;
    this.lean += (targetLean - this.lean) * Math.min(1, LEAN_RATE * dt);

    const sample = slope.sampleAtZ(this.position.z);
    const slopeForwardX = sample.forwardX;
    const slopeForwardZ = sample.forwardZ;

    const horizSpeed = Math.sqrt(
      this.velocity.x * this.velocity.x + this.velocity.z * this.velocity.z
    );

    this.heading += TURN_GAIN * this.lean * horizSpeed * dt;

    const slopeHeading = Math.atan2(slopeForwardX, slopeForwardZ);
    const headingErr = wrapAngle(this.heading - slopeHeading);
    const maxHeadingDeviation = 0.4;
    if (headingErr > maxHeadingDeviation) {
      this.heading = slopeHeading + maxHeadingDeviation;
    } else if (headingErr < -maxHeadingDeviation) {
      this.heading = slopeHeading - maxHeadingDeviation;
    }

    const fx = Math.sin(this.heading);
    const fz = Math.cos(this.heading);

    const downhillBoost = Math.max(0, slopeForwardZ);
    const accel = GRAVITY * 0.32 * downhillBoost;
    this.velocity.x += fx * accel * dt;
    this.velocity.z += fz * accel * dt;

    const vAlong = this.velocity.x * fx + this.velocity.z * fz;
    const vPerp = this.velocity.x * fz - this.velocity.z * fx;
    const gripFactor = Math.min(1, LATERAL_GRIP * dt);
    const vPerpAfterGrip = vPerp * (1 - gripFactor);
    const vAlongAfterScrub =
      vAlong * Math.max(0, 1 - SCRUB_GAIN * Math.abs(this.lean) * dt);
    this.velocity.x = vAlongAfterScrub * fx + vPerpAfterGrip * fz;
    this.velocity.z = vAlongAfterScrub * fz - vPerpAfterGrip * fx;

    const lateralOffset = this.position.x - sample.centerX;
    const distFromCenter = Math.abs(lateralOffset);
    if (distFromCenter > slope.groomedHalfWidth) {
      const depth = distFromCenter - slope.groomedHalfWidth;
      const slopeRightX = sample.rightX;
      const slopeRightZ = sample.rightZ;
      const vFwd = this.velocity.x * slopeForwardX + this.velocity.z * slopeForwardZ;
      const vLat = this.velocity.x * slopeRightX + this.velocity.z * slopeRightZ;
      const fwdDrag = Math.min(1, POWDER_FWD_DRAG * dt * (1 + depth * 0.15));
      const latDrag = Math.min(1, POWDER_LAT_DRAG * dt * (1 + depth * 0.3));
      const vFwdNew = vFwd * (1 - fwdDrag);
      const vLatNew = vLat * (1 - latDrag);
      this.velocity.x = vFwdNew * slopeForwardX + vLatNew * slopeRightX;
      this.velocity.z = vFwdNew * slopeForwardZ + vLatNew * slopeRightZ;
      const push = -Math.sign(lateralOffset) * POWDER_PUSH * Math.min(depth, 5) * dt;
      this.velocity.x += push;
    }

    const horizNew = Math.sqrt(
      this.velocity.x * this.velocity.x + this.velocity.z * this.velocity.z
    );
    if (horizNew > MAX_SPEED) {
      const k = MAX_SPEED / horizNew;
      this.velocity.x *= k;
      this.velocity.z *= k;
    }

    this.velocity.y -= GRAVITY * dt;

    this.position.x += this.velocity.x * dt;
    this.position.y += this.velocity.y * dt;
    this.position.z += this.velocity.z * dt;

    const surfaceSample = slope.sampleAtZ(this.position.z);
    const minY = surfaceSample.surfaceY + BOARD_OFFSET;
    if (this.position.y <= minY) {
      this.position.y = minY;
      if (this.velocity.y < 0) {
        this.velocity.y = 0;
      }
      this.onGround = true;
    } else {
      this.onGround = false;
    }

    this.root.position.copyFrom(this.position);
    this.modelRoot.rotationQuaternion = Quaternion.RotationYawPitchRoll(
      this.heading,
      Math.max(-0.25, Math.min(0.25, -slopeForwardZ * 0.0)),
      -this.lean
    );
  }

  carveIntensity(): number {
    const speed = Math.sqrt(
      this.velocity.x * this.velocity.x + this.velocity.z * this.velocity.z
    );
    return Math.abs(this.lean) * Math.min(1, speed / 12);
  }

  outsideEdgeOffset(): Vector3 {
    const sideSign = this.lean >= 0 ? -1 : 1;
    const fx = Math.sin(this.heading);
    const fz = Math.cos(this.heading);
    const rx = fz;
    const rz = -fx;
    return new Vector3(rx * sideSign * 0.35, 0.05, rz * sideSign * 0.35);
  }

}

void RIDER_HEIGHT_PIVOT;

function wrapAngle(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}
